import { useMutation } from '@apollo/client';
import { Loader2, ShieldOff } from 'lucide-react';
import { useState } from 'react';

import { RESET_CUSTOMER_TWO_FA } from '@/apollo/schemas/customers/resetCustomerTwoFA';
import { AdminActionWrapper } from '@/components/common/AdminActionWrapper';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/CoginitoAuthProvider';
import { useToast } from '@/hooks/use-toast';

interface Reset2FADialogProps {
  customerId: string;
  customerName: string;
}

const Reset2FADialog = ({ customerId, customerName }: Reset2FADialogProps) => {
  const { toast } = useToast();
  const { session } = useAuth();
  const [open, setOpen] = useState(false);

  const [resetTwoFA, { loading }] = useMutation(RESET_CUSTOMER_TWO_FA, {
    onCompleted: (data) => {
      const result = data?.resetCustomerTwoFA;
      if (result?.status) {
        toast({
          variant: 'success',
          title: '2FA reset successfully',
          description: `${customerName} can now re-enroll their authenticator app.`,
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Reset failed',
          description: result?.message ?? 'An unexpected error occurred.',
        });
      }
      setOpen(false);
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to reset 2FA',
        description: error.message,
      });
    },
  });

  const handleReset = () => {
    resetTwoFA({
      variables: { input: { customerId } },
      context: {
        headers: { identity: session?.getIdToken().getJwtToken() },
      },
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 data-[state=open]:bg-accent"
          >
            <ShieldOff className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <AdminActionWrapper>
            <DropdownMenuItem
              onClick={() => setOpen(true)}
              className="text-amber-600 focus:text-amber-600"
            >
              <ShieldOff className="mr-2 h-3.5 w-3.5" />
              Reset 2FA
            </DropdownMenuItem>
          </AdminActionWrapper>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[425px] bg-white dark:bg-foreground dark:text-white">
          <DialogHeader>
            <DialogTitle>Reset Two-Factor Authentication</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will clear{' '}
            <span className="font-medium text-foreground">{customerName}</span>
            &apos;s current 2FA enrollment. They will be able to log in with
            their password and set up a new authenticator app from scratch.
          </p>
          <p className="text-sm font-medium text-amber-600">
            Are you sure you want to reset 2FA for this customer?
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleReset}
              disabled={loading}
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reset 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Reset2FADialog;
